/**
 * Reads the `account_deletions` audit trail.
 *
 * Read-only by design: firestore.rules gives admins `allow read` and sets
 * `allow write: if false` on this collection, so there is no mutation to write here.
 * Recovery is the hourly `retryAccountDeletions` function's job.
 *
 * Every query below is served by an index that already exists. Two of them are load-
 * bearing enough to state:
 *
 *   getRecent()  orders by updatedAt DESC with NO status filter, and the status
 *                filtering happens in the browser. Combining `where('status',...)`
 *                with `orderBy('updatedAt','desc')` would need a NEW composite index --
 *                the one in firestore.indexes.json is (status ASC, updatedAt ASC).
 *                Deletion volume is low, so filtering client-side is cheaper than
 *                adding an index.
 *
 *   countStalled() uses exactly the shape findStalledDeletions() uses in
 *                obsessless-cloud-functions/src/users/deletionAudit.ts, which is what
 *                the existing composite index was added for.
 */
import {
    collection,
    getCountFromServer,
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type {
    AccountDeletionRecord,
    DeletionHealthCounts,
    DeletionStatus,
} from '../types/accountDeletionTypes';

const COLLECTION = 'account_deletions';

/** The page reads this many most-recently-updated records. */
export const RECENT_LIMIT = 500;

/**
 * How long a job may sit untouched before the UI calls it stalled.
 *
 * The reconciler uses 15 minutes (STALE_AFTER_MINUTES in retryAccountDeletions.ts),
 * but that is the point at which it is ALLOWED to retry, not the point at which a
 * human should look. A job picked up on the next hourly run is working as designed.
 * Two hours means at least one reconciler run has already failed to finish it.
 */
export const STALLED_AFTER_MS = 2 * 60 * 60 * 1000;

/** Not yet finished, so still eligible for the reconciler. */
const UNFINISHED_STATUSES: DeletionStatus[] = ['in_progress', 'failed'];

class AccountDeletionsService {
    private static instance: AccountDeletionsService;

    private constructor() {}

    public static getInstance(): AccountDeletionsService {
        if (!AccountDeletionsService.instance) {
            AccountDeletionsService.instance = new AccountDeletionsService();
        }
        return AccountDeletionsService.instance;
    }

    /**
     * The most recently updated records, newest first.
     *
     * Fields are read defensively rather than cast: a document written by an older
     * version of the function, or seeded by a tools/ script, can be missing the newer
     * ones. A missing `firestore` map must read as "step 3 never ran", not crash the
     * table.
     */
    async getRecent(max: number = RECENT_LIMIT): Promise<AccountDeletionRecord[]> {
        const snapshot = await getDocs(
            query(collection(db, COLLECTION), orderBy('updatedAt', 'desc'), limit(max))
        );

        return snapshot.docs.map((document) => {
            const data = document.data();
            return {
                uid: document.id,
                status: data.status as DeletionStatus,
                source: data.source,
                requestedAt: data.requestedAt,
                updatedAt: data.updatedAt,
                completedAt: data.completedAt ?? null,
                attemptCount: data.attemptCount ?? 0,
                registryVersion: data.registryVersion ?? 0,
                revenueCat: data.revenueCat ?? null,
                posthog: data.posthog ?? null,
                firestore: data.firestore ?? {},
            } as AccountDeletionRecord;
        });
    }

    /**
     * Server-side counts, so the page header stays true past the getRecent() cap.
     *
     * Aggregation queries are billed by index entries read, not documents, so this is
     * cheap enough to run on the dashboard too.
     */
    async getHealthCounts(): Promise<DeletionHealthCounts> {
        const [inProgress, completed, failed, stalled] = await Promise.all([
            this.countByStatus('in_progress'),
            this.countByStatus('completed'),
            this.countByStatus('failed'),
            this.countStalled(),
        ]);

        return { inProgress, completed, failed, stalled };
    }

    private async countByStatus(status: DeletionStatus): Promise<number> {
        const snapshot = await getCountFromServer(
            query(collection(db, COLLECTION), where('status', '==', status))
        );
        return snapshot.data().count;
    }

    /** Mirrors findStalledDeletions() in deletionAudit.ts, with the UI threshold. */
    private async countStalled(): Promise<number> {
        const staleBefore = Timestamp.fromMillis(Date.now() - STALLED_AFTER_MS);
        const snapshot = await getCountFromServer(
            query(
                collection(db, COLLECTION),
                where('status', 'in', UNFINISHED_STATUSES),
                where('updatedAt', '<', staleBefore),
                orderBy('updatedAt')
            )
        );
        return snapshot.data().count;
    }
}

export const accountDeletionsService = AccountDeletionsService.getInstance();
